import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BackendService, BaseDataService, EOperatore, GetPagesRequest, GetPagesResponse, GetWebsitesResponse, IApiRequest, Languages, MenuData, PageLink, PageLinkDetails, UserService, WebsiteDef, WebsiteLanguage } from 'nr-common';

import { environment } from '../../environments/environment';
import { ApiService } from './api.service';
import { OperatorLink } from '../shared/models/operatorLink';

type SITE_ACRONIMS = 'IT' | 'FR' | 'DE' | 'NL';

@Injectable()
export class WebsiteService {
	// Flag interni di sincronizzazione
	public currentWebsite: WebsiteDef;
	public showAdvancedControls$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	private websites: WebsiteDef[];
	private _currentOperator: EOperatore = EOperatore.Indefinito;
	private sitePages: GetPagesResponse = null;

	public operatorLinks: OperatorLink[] = [];

	constructor(
		private user: UserService,
		private backend: BackendService,
		private translate: TranslateService,
		private basedata: BaseDataService
	) {}

	public async init() {
		// Load the websites
		const request: IApiRequest = {};
		const response: GetWebsitesResponse = await this.backend.post('websites/get', request);
		this.websites = response.websites;

		this.setWebsite();

		this.translate.addLangs(['it', 'en', 'fr', 'es', 'de', 'nl']);
		//this.translate.setDefaultLang(this.currentWebsite.defaultLangCode);
		this.translate.use(this.currentWebsite.defaultLangCode);

		const req = new GetPagesRequest({
			lingua: this.basedata.getLingua(),
			operator: this._currentOperator,
		});
		this.sitePages = await this.backend.post('b2c/pages', req);

		this.showAdvancedControls$.next(this.user.userPermissions?.intranet ?? false);

		this.setOperatorsAttributes();
	}

	public initOffline() {
		this.currentWebsite = new WebsiteDef({
			id: 3,
			host: 'b2c.naar.com,staging.naar.com',
			defaultLangCode: 'it',
			operatore: EOperatore.Naar,
			availableLanguages: [],
		})
	}

	private async setWebsite() {
		// PRODUCTION
		if (typeof environment.website === 'string' && environment.website === 'auto') {
			/* if (this._currentOperator == EOperatore.Indefinito) {
                this._currentOperator = EOperatore.Naar;
            } */
			//let website = this.websites.find((w) => w.operatore == this._currentOperator);

			// Get the website from the URL
			let website = this.websites.find((w) => w.host === window.location.hostname);
			if (!website) {
				website = this.websites[0];
			}
			this.currentWebsite = website;
		} else {
			// DEVELOP: website is fixed in the environment
			this.currentWebsite = this.websites.find((ws) => ws.id == +environment.website);
		}

		this._currentOperator = this.currentWebsite.operatore;
	}

	public setLanguage(language: WebsiteLanguage) {
		this.translate.use(language.codice);
	}

	public formatPageTitle(pageName: string, siteName: string = null): string {
		// console.log("formatPageTitle", `${siteName} - ${pageName}`);
		return `${siteName} - ${pageName}`;
	}

	// Restituisce la chiave per il titolo della pagina in lingua
	public getPageTitleKey(pageName: string): string {
		if (!pageName || pageName === '' || pageName.length === 0) {
			console.warn(`Invalid pageName: "${pageName}".`);
			return '';
		}
		pageName = this.getValidTranslationKey(pageName);
		return `PAGES.${pageName}.TITLE`;
	}

	public getValidTranslationKey(keyName: string): string {
		return keyName.trim().toUpperCase().replace(' ', '-');
	}

	/**
	 * Traduce il nome di pagina delle rotte Angular in id numerico del CMS
	 * @param pageName nome della pagina usata come route di Angular
	 * @returns id numerico della tabella PageLink (per il CMS)
	 */

	public getPageLinkId(pageName: string): number {
		if (this.sitePages == null || this.sitePages.pages == null) return 0;

		const page = this.sitePages.pages.find((p) => p.nome.toLowerCase() === pageName.toLowerCase());
		if (page == null) return 0;
		return page.id;
	}

	/**
	 * Restituisce l'ID del footer in funzione della lingua (sito)
	 * @returns id numerico della tabella PageLink afferente al footer (per il CMS)
	 */
	public getFooterLinkId(): number {
		if (this.sitePages == null || this.sitePages.footer == null) return 0;
		return this.sitePages.footer?.id || 0;
	}

	public getInfostrip(): MenuData[] {
		if (this.sitePages == null || this.sitePages.secondMenu == null) return [];

		return this.sitePages.secondMenu?.map((m) => {
			return new MenuData({
				icon: m.faIcon,
				nome: m.nome,
				idLuogo: m.luogoID,
				idPagelink: m.id,
				url: m.url,
			});
		});
	}

	/**
	 * Gestisco la lista degli operatori disponibili (semicablone)
	 * e la traduzione dei loro nomi
	 */
	public setOperatorsAttributes() {

		// uso basedataservice per ottenere la lista degli operatori
		this.basedata
			.getOperatorsDropdown$()
			.subscribe((operators) => {
				// console.log('operators', operators);
				operators.map((operator) => {
					this.operatorLinks.push(new OperatorLink(operator.value, operator.text));
			});
		});
	}
}
